import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraActionRow, SpecraButton, SpecraEyebrow, SpecraText } from '@mono/ui-components';
import { AlertOctagon, AlertTriangle, FileCheck, ShieldAlert, Stamp } from 'lucide-react';
import { useState } from 'react';
import { contractDaysFromToday, formatContractDate, NO_CONTRACT_DATE } from '../../../lib/contractDate';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { EntityFormDialog } from '../../../pages/Delivery/_shared/components/EntityFormDialog';
import { useResolveCorrectiveAction } from '../../../pages/Delivery/Compliance/hooks';
import { cmDate } from '../../../pages/Delivery/Deliverables/lib/format';
import { useMyAssignedActions, useMyDeliverableActions } from './useMyAssignedActions';
// Mirrors ryuguv1.AssignedActionKind.ASSIGNED_ACTION_KIND_CORRECTIVE_ACTION
// (see KIND_ICON/KIND_LABEL below).
export const CORRECTIVE_ACTION_KIND = 3;
// A Correction only accepts a new resolution while it's still with its
// assignee — "Resolved" is already awaiting the auditor's verification, and
// terminal states never reach this feed at all (ListMyAssignedActions'
// backing query excludes Resolved/Verified/Cancelled).
const RESOLVABLE_CORRECTION_STATES = new Set([
    'Open',
    'In Progress'
]);
function isResolvableCorrection(action) {
    return action.kind === CORRECTIVE_ACTION_KIND && RESOLVABLE_CORRECTION_STATES.has(action.state);
}
const KIND_ICON = {
    1: /*#__PURE__*/ _jsx(FileCheck, {
        className: "h-3.5 w-3.5"
    }),
    2: /*#__PURE__*/ _jsx(AlertTriangle, {
        className: "h-3.5 w-3.5"
    }),
    3: /*#__PURE__*/ _jsx(ShieldAlert, {
        className: "h-3.5 w-3.5"
    }),
    4: /*#__PURE__*/ _jsx(AlertOctagon, {
        className: "h-3.5 w-3.5"
    })
};
// Product vocabulary (D8 renamed Risks -> Potential Issues): the row's kind
// label must match what the destination page calls itself, not the proto
// enum name.
const KIND_LABEL = {
    1: 'Deliverable',
    2: 'Issue',
    3: 'Correction',
    4: 'Potential issue'
};
// `dueAt` is an optional proto Timestamp — unset or malformed must always
// degrade to "No due date", never a raw NaN/Invalid Date render.
function formatDueDate(dueAt) {
    const formatted = formatContractDate(dueAt);
    return formatted === NO_CONTRACT_DATE ? 'No due date' : formatted;
}
function dueDateTone(dueAt) {
    if (!dueAt) return 'neutral';
    const days = contractDaysFromToday(dueAt);
    if (days === null) return 'neutral';
    if (days < 0) return 'danger';
    return days <= 7 ? 'warning' : 'neutral';
}
// The deliverable kind on ryugu's enum; its rows now come from the contract
// model (useMyDeliverableActions), never from ListMyAssignedActions.
const DELIVERABLE_KIND = 1;
function actionRow(a) {
    return {
        key: `${a.kind}:${a.id}`,
        icon: KIND_ICON[a.kind],
        kindLabel: KIND_LABEL[a.kind],
        title: a.title,
        state: a.state,
        due: formatDueDate(a.dueAt),
        tone: dueDateTone(a.dueAt),
        href: a.destinationPath,
        action: a
    };
}
function deliverableRow(d) {
    return {
        key: d.key,
        icon: d.kind === 'approval' ? /*#__PURE__*/ _jsx(Stamp, {
            className: "h-3.5 w-3.5"
        }) : KIND_ICON[DELIVERABLE_KIND],
        kindLabel: d.kind === 'approval' ? 'Approval' : KIND_LABEL[DELIVERABLE_KIND],
        title: d.title,
        state: d.state,
        due: cmDate(d.dueOn, 'No due date'),
        tone: d.tone,
        href: d.href
    };
}
// Dedupe by key so a row that reaches the client via more than one path in
// the response can never render twice.
function dedupeRows(rows) {
    const seen = new Set();
    const out = [];
    for (const r of rows){
        if (seen.has(r.key)) continue;
        seen.add(r.key);
        out.push(r);
    }
    return out;
}
/**
 * Renders one caller's ListMyAssignedActions feed, scoped to a program
 * (SAD-170/D15, SAD-179) or tenant-wide (SAD-143). Shared by Program Home's
 * Mine group, the Workroom signals panel, and the global dashboard so no two
 * surfaces can drift on rendering rules — only the cap, heading, testids, and
 * enableResolve differ per caller. On a program, the caller's deliverable
 * dates and approvals (contract model, D113) lead the list.
 */ export function AssignedActionList({ programId, heading, cap, testids, enableResolve, onlyKinds, emptyText }) {
    const feed = useMyAssignedActions(programId);
    // D113: the caller's deliverable dates and approvals, from the contract
    // model. Skipped on a surface whose kinds leave deliverables out.
    const deliverables = useMyDeliverableActions(programId, {
        enabled: !onlyKinds || onlyKinds.includes(DELIVERABLE_KIND)
    });
    const { data } = feed;
    const isLoading = feed.isLoading || deliverables.isLoading;
    // One feed failing never hides the other's rows; both failing is the error.
    const isError = feed.isError && (deliverables.isError || deliverables.rows.length === 0);
    const partialError = !isError && (feed.isError || deliverables.isError);
    const [resolving, setResolving] = useState(null);
    const resolveCorrectiveAction = useResolveCorrectiveAction();
    const headingEl = heading ? /*#__PURE__*/ _jsx(SpecraEyebrow, {
        className: "block",
        children: heading
    }) : null;
    // No linked contact record => no personal queue at all. A message on every
    // program would be noise for a caller this simply doesn't apply to. Guarded
    // to the settled case only — `data` is undefined while loading or on error,
    // and returning null then would silently drop the loading/error state (and
    // any dialog this component has open) instead of showing it.
    const noLinkedContact = !isLoading && !isError && !data?.callerHasLinkedContact && deliverables.rows.length === 0;
    const kindFiltered = data ? onlyKinds ? data.actions.filter((a)=>onlyKinds.includes(a.kind)) : data.actions : [];
    const deduped = dedupeRows([
        ...deliverables.rows.map(deliverableRow),
        ...kindFiltered.map(actionRow)
    ]);
    const visible = deduped.slice(0, cap);
    const overflowCount = Math.max(0, deduped.length - cap);
    // A kind-filtered surface must not advertise counts drawn from the whole
    // feed: "3 more actions not shown" would be describing deliverables and risks
    // this view is deliberately not about.
    const hiddenCount = data ? onlyKinds ? 0 : data.hiddenCount : 0;
    const showMore = overflowCount > 0 || !onlyKinds && !!data?.hasMore || hiddenCount > 0;
    const morePieces = [];
    if (overflowCount > 0) {
        morePieces.push(`${overflowCount} more ${overflowCount === 1 ? 'action' : 'actions'} not shown`);
    } else if (!onlyKinds && data?.hasMore) {
        morePieces.push('More assigned actions are not shown');
    }
    if (hiddenCount > 0) {
        morePieces.push(`${hiddenCount} hidden because you don’t have access to ${hiddenCount === 1 ? 'its program' : 'their programs'}`);
    }
    if (noLinkedContact) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-5",
        "data-testid": testids.container,
        children: [
            headingEl,
            isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
                className: "mt-2 h-16 w-full"
            }) : isError ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                tone: "muted",
                className: "mt-2",
                "data-testid": testids.error,
                children: "Couldn't load your assigned actions."
            }) : visible.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                tone: "muted",
                className: "mt-2",
                "data-testid": testids.empty,
                children: emptyText ?? (programId ? 'Nothing is assigned to you on this program.' : 'Nothing is assigned to you.')
            }) : /*#__PURE__*/ _jsx("div", {
                className: "mt-1 divide-y divide-border",
                children: visible.map((row)=>{
                    const { action } = row;
                    // SAD-143: a Correction row on a resolve-enabled surface (the
                    // global dashboard) never links into the program's audits page —
                    // the caller may hold no view on that program at all. It renders
                    // as a plain row, with a Resolve button standing in for
                    // navigation while the correction is still actionable.
                    const inert = enableResolve && action?.kind === CORRECTIVE_ACTION_KIND;
                    return /*#__PURE__*/ _jsx(SpecraActionRow, {
                        "data-testid": testids.row,
                        "data-tone": row.tone,
                        renderLink: renderRouterLink,
                        trailing: null,
                        item: {
                            id: row.key,
                            icon: row.icon,
                            tone: row.tone,
                            label: /*#__PURE__*/ _jsx("span", {
                                title: row.title,
                                children: row.title
                            }),
                            // Kind and state truncate first; the due date is load-bearing
                            // (SAD-179 journey asserts on it) and rides in meta, never
                            // truncated.
                            description: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        title: row.kindLabel,
                                        children: row.kindLabel
                                    }),
                                    ' · ',
                                    /*#__PURE__*/ _jsx("span", {
                                        title: row.state,
                                        children: row.state
                                    })
                                ]
                            }),
                            meta: row.due,
                            href: inert ? undefined : row.href,
                            actions: inert && action && isResolvableCorrection(action) ? /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: ()=>setResolving(action),
                                "data-testid": testids.resolve,
                                children: "Resolve"
                            }) : undefined
                        }
                    }, row.key);
                })
            }),
            partialError ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                className: "mt-2",
                "data-testid": testids.error,
                children: "Some of your assigned actions couldn't load."
            }) : null,
            showMore ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                className: "mt-2",
                "data-testid": testids.more,
                children: morePieces.join(' · ')
            }) : null,
            enableResolve ? /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: resolving !== null,
                onOpenChange: (open)=>open ? undefined : setResolving(null),
                title: "Resolve Correction",
                fields: [
                    {
                        kind: 'textarea',
                        name: 'note',
                        label: 'Resolution note',
                        rows: 3
                    }
                ],
                defaultValues: {
                    note: ''
                },
                onSubmit: async (v)=>{
                    if (resolving) {
                        await resolveCorrectiveAction.mutateAsync({
                            correctiveActionId: resolving.id,
                            resolutionNote: v.note
                        });
                    }
                    setResolving(null);
                },
                isSubmitting: resolveCorrectiveAction.isPending,
                submitLabel: "Mark Resolved"
            }) : null
        ]
    });
}
